// DHTN Sans 1.200 is a static cut of Google Sans Flex at optical size 18.
//
// Reading text: body spacing is preserved (half of the 1.000 tracking; word gaps kept).
//
// Button labels (sentence case since W1, 26 Sep 2026) follow the type designer's own optical-size
// curve, measured from the variable Google Sans Flex (ROND 60, wdth 96, wght 700): the sidebearings
// grow by 0.0030em for every px below 18 (0.0060em at 16px) and stop changing above 18. So a
// button label gets 0.003em x (18 - size), and none at 18px and larger. Word gaps stay the font's own.
function previousTrackingEm(size: number, weight: number): number {
    const t = Math.min(1, Math.max(0, (size - 12) / 12))
    return .02 * (1 - t) ** 3 + (weight >= 600 ? .005 * (1 - t) : 0)
}

export function trackingEm(size: number, weight: number, button = false): number {
    if (button) return Math.max(0, .003 * (18 - size))
    return previousTrackingEm(size, weight) * .5
}

export function wordSpacingEm(size: number, weight: number, button = false): number {
    if (button) return 0
    // Reading text retains its existing word gaps.
    return previousTrackingEm(size, weight) - trackingEm(size, weight)
}

const arabic = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb50-\ufdff\ufe70-\ufeff]/
const excluded = 'svg,canvas,pre,code,kbd,samp,.font-scripture,[data-type-spacing="manual"]'

// Inline-style declarations that can change a font's size, weight, family or spacing. Custom
// properties count too: one may feed a font size. Transform and opacity writes (the subject strip
// and the living paintings write them every frame) do not.
const fontProperty = /^(font|letter-spacing|word-spacing|--)/
function fontDeclarations(style: string | null): string {
    return (style ?? '').split(';').map(d => d.trim())
        .filter(d => fontProperty.test(d.slice(0, d.indexOf(':')).trim().toLowerCase())).join(';')
}

/** True when a style attribute change touches a font-related declaration. */
export function fontStyleChanged(before: string | null, after: string | null): boolean {
    return fontDeclarations(before) !== fontDeclarations(after)
}

/** Apply to rendered text, including new route content and responsive text sizes.
 * Mutation work is batched and limited to changed subtrees. Own writes are ignored, and so are style
 * changes that touch no font declaration. All reads run before any write, so style is computed once.
 */
export function installTypography(): () => void {
    const pending = new Set<HTMLElement>()
    let frame = 0
    let disposed = false
    const update = (root: HTMLElement) => {
        const nodes = [root, ...root.querySelectorAll<HTMLElement>('p,span,a,button,label,input,textarea,select,small,strong,em,h1,h2,h3,h4,h5,h6,li,td,th,div')]
        // Pass 1, reads only. A write between two reads makes the browser recompute style again.
        // (The writes below touch only letter and word spacing, never what pass 1 reads.)
        const found: { el: HTMLElement; tracking: string; gap: string }[] = []
        for (const el of nodes) {
            if (!el.isConnected || el.closest(excluded)) continue
            const directText = [...el.childNodes].filter(n => n.nodeType === Node.TEXT_NODE).map(n => n.textContent).join('')
            if (!directText.trim() && !el.matches('input,textarea,select,button')) continue
            const cs = getComputedStyle(el)
            const eligible = cs.fontFamily.includes('DHTN Sans') && !el.matches(':lang(ar)') && !arabic.test(directText)
            if (!eligible) { found.push({ el, tracking: '', gap: '' }); continue }
            const button = !!el.closest('[data-dhtn-action]')
            const size = parseFloat(cs.fontSize)
            const weight = parseInt(cs.fontWeight) || 400
            found.push({ el, tracking: trackingEm(size, weight, button) + 'em', gap: wordSpacingEm(size, weight, button) + 'em' })
        }
        // Pass 2, writes only.
        for (const { el, tracking, gap } of found) {
            if (!tracking) {
                el.removeAttribute('data-dhtn-tracked')
                el.style.removeProperty('--dhtn-tracking')
                el.style.removeProperty('--dhtn-word-spacing')
                continue
            }
            if (el.style.getPropertyValue('--dhtn-tracking') !== tracking) el.style.setProperty('--dhtn-tracking', tracking)
            if (el.style.getPropertyValue('--dhtn-word-spacing') !== gap) el.style.setProperty('--dhtn-word-spacing', gap)
            if (!el.hasAttribute('data-dhtn-tracked')) el.setAttribute('data-dhtn-tracked', '')
        }
    }
    const flush = () => {
        frame = 0
        observer.disconnect()
        const roots = [...pending]; pending.clear()
        for (const root of roots) if (!roots.some(other => other !== root && other.contains(root))) update(root)
        observe()
    }
    const queue = (root: HTMLElement) => {
        if (disposed) return
        pending.add(root)
        if (!frame) frame = requestAnimationFrame(flush)
    }
    const observer = new MutationObserver(records => {
        for (const record of records) {
            const target = record.target instanceof HTMLElement ? record.target : record.target.parentElement
            if (!target) continue
            // Compared with the current value, so several writes in one batch are judged by their net effect.
            if (record.attributeName === 'style' && !fontStyleChanged(record.oldValue, (record.target as Element).getAttribute('style'))) continue
            queue(target)
        }
    })
    const observe = () => observer.observe(document.documentElement, { subtree: true, childList: true, characterData: true, attributes: true, attributeOldValue: true, attributeFilter: ['class', 'style', 'lang', 'dir'] })
    const refresh = () => queue(document.body)
    observe(); refresh()
    window.addEventListener('resize', refresh)
    document.fonts.addEventListener('loadingdone', refresh)
    return () => {
        disposed = true; observer.disconnect(); cancelAnimationFrame(frame)
        window.removeEventListener('resize', refresh)
        document.fonts.removeEventListener('loadingdone', refresh)
    }
}
